<template>
  <footer class="footer" @click.self="closePopup">
    <div class="footer-container">

      <!-- ── TOP ROW ── -->
      <div class="footer-top">

        <!-- Colonne gauche : logo + adresses + contact -->
        <div class="footer-left">
          <img :src="logoImage" alt="Nolido Business" class="footer-logo" />

          <ul class="footer-addresses">
            <li v-for="addr in addresses" :key="addr.text" class="footer-address">
              <span class="addr-flag" aria-hidden="true">{{ addr.flag }}</span>
              <span class="addr-text">{{ addr.text }}</span>
            </li>
          </ul>

          <div class="footer-contact">
            <a href="mailto:hello@nolido.cash" class="contact-item">
              <i class="far fa-envelope" aria-hidden="true"></i>
              hello@nolido.cash
            </a>
            <span class="contact-sep" aria-hidden="true"></span>
            <a href="tel:+221338310366" class="contact-item">
              <i class="fas fa-phone" aria-hidden="true"></i>
              (+221) 33 831 03 66 – 77 866 96 96
            </a>
          </div>
        </div>

        <!-- Colonne droite : bouton expert + app stores + socials -->
        <div class="footer-right">

          <!-- ── Bouton Parler à un expert + Popup flottant ── -->
          <div class="expert-wrap" ref="expertWrap">
            <button
              class="btn-expert"
              :class="{ 'is-active': showExpertPopup }"
              @click.stop="toggleExpertPopup"
              aria-haspopup="true"
              :aria-expanded="showExpertPopup"
            >
              <i class="fas fa-phone" aria-hidden="true"></i>
              Parler à un expert
            </button>

            <!-- Popup flottant — ancré en haut à droite du bouton -->
            <transition name="popup-drop">
              <div
                v-if="showExpertPopup"
                class="expert-popup"
                role="dialog"
                aria-label="Coordonnées expert Nolido"
                @click.stop
              >
                <!-- ① WhatsApp -->
                <div class="popup-row">
                  <div class="popup-row__icon popup-row__icon--whatsapp">
                    <i class="fab fa-whatsapp"></i>
                  </div>
                  <div class="popup-row__info">
                    <span class="popup-row__title">WhatsApp</span>
                    <span class="popup-row__sub">Discuter sur WhatsApp</span>
                  </div>
                  <a
                    href="https://wa.me/22177868686"
                    target="_blank"
                    rel="noopener"
                    class="popup-action-btn"
                    @click="closePopup"
                  >
                    Ouvrir
                    <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                  </a>
                </div>

                <!-- ② Téléphone -->
                <div class="popup-row">
                  <div class="popup-row__icon popup-row__icon--phone">
                    <i class="fas fa-mobile-alt"></i>
                  </div>
                  <div class="popup-row__info">
                    <span class="popup-row__title">77 868 86 86</span>
                    <span class="popup-row__sub">Numéro de téléphone</span>
                  </div>
                  <button
                    class="popup-action-btn"
                    @click="copyPhone"
                    :class="{ 'is-copied': phoneCopied }"
                  >
                    <template v-if="phoneCopied">
                      Copié <i class="fas fa-check" aria-hidden="true"></i>
                    </template>
                    <template v-else>
                      Copier <i class="far fa-copy" aria-hidden="true"></i>
                    </template>
                  </button>
                </div>

                <!-- ③ Email -->
                <div class="popup-row popup-row--last">
                  <div class="popup-row__icon popup-row__icon--email">
                    <i class="far fa-envelope"></i>
                  </div>
                  <div class="popup-row__info">
                    <span class="popup-row__title">hello@nolido.cash</span>
                    <span class="popup-row__sub">Adresse mail</span>
                  </div>
                  <a
                    href="mailto:hello@nolido.cash"
                    class="popup-action-btn"
                    @click="closePopup"
                  >
                    Envoyer un mail
                  </a>
                </div>
              </div>
            </transition>
          </div>

          <!-- App Store + Play Store -->
          <div class="app-buttons">
            <a href="#" class="app-button">
              <i class="fab fa-apple" aria-hidden="true"></i>
              <div class="btn-text">
                <span class="btn-text__small">TÉLÉCHARGER SUR</span>
                <span class="btn-text__large">App Store</span>
              </div>
            </a>
            <a href="#" class="app-button">
              <i class="fab fa-google-play" aria-hidden="true"></i>
              <div class="btn-text">
                <span class="btn-text__small">TÉLÉCHARGER SUR</span>
                <span class="btn-text__large">Play Store</span>
              </div>
            </a>
          </div>

          <!-- Réseaux sociaux -->
          <div class="social-icons">
            <a href="https://www.facebook.com/nolido" target="_blank" rel="noopener" class="social-icon" aria-label="Facebook">
              <i class="fab fa-facebook-f"></i>
            </a>
            <a href="https://www.linkedin.com/company/nolido" target="_blank" rel="noopener" class="social-icon" aria-label="LinkedIn">
              <i class="fab fa-linkedin-in"></i>
            </a>
            <a href="https://twitter.com/nolido" target="_blank" rel="noopener" class="social-icon" aria-label="X / Twitter">
              <i class="fab fa-x-twitter"></i>
            </a>
            <a href="https://www.instagram.com/nolido" target="_blank" rel="noopener" class="social-icon" aria-label="Instagram">
              <i class="fab fa-instagram"></i>
            </a>
          </div>

        </div>
      </div>

      <!-- ── BOTTOM : copyright centré ── -->
      <div class="footer-bottom">
        <p class="footer-copyright">© {{ currentYear }} Nolido Cash</p>
      </div>

    </div>
  </footer>
</template>

<script>
export default {
  name: 'AppFooter',

  data() {
    return {
      logoImage:       require('@/assets/img/logo.png'),
      currentYear:     new Date().getFullYear(),
      showExpertPopup: false,
      phoneCopied:     false,

      addresses: [
        { flag: '🇧🇯', text: 'Bénin : Ilot 812 Fignon Sikè, Cotonou, Bénin' },
        { flag: '🇸🇳', text: 'Sénégal : Immeuble Dabo, Avenue liberté 6 Extension lot 30, Dakar, Sénégal' },
        { flag: '🇹🇬', text: "Togo : Lomé-Kagomé à 200m du terrain d'Olympio Maison ATiYE" },
      ],
    }
  },

  mounted() {
    // Ferme le popup si on clique ailleurs dans la page
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  methods: {
    toggleExpertPopup() {
      this.showExpertPopup = !this.showExpertPopup
      if (!this.showExpertPopup) this.phoneCopied = false
    },

    closePopup() {
      this.showExpertPopup = false
      this.phoneCopied = false
    },

    handleOutsideClick(e) {
      const wrap = this.$refs.expertWrap
      if (wrap && !wrap.contains(e.target)) {
        this.closePopup()
      }
    },

    async copyPhone() {
      try {
        await navigator.clipboard.writeText('77 868 86 86')
        this.phoneCopied = true
        setTimeout(() => { this.phoneCopied = false }, 2500)
      } catch {
        // Fallback pour anciens navigateurs
        const el = document.createElement('textarea')
        el.value = '77 868 86 86'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this.phoneCopied = true
        setTimeout(() => { this.phoneCopied = false }, 2500)
      }
    },
  },
}
</script>

<style scoped>
/* ════════════════════════════════════════════════
   TOKENS
════════════════════════════════════════════════ */
.footer {
  --font:               'Sora', 'DM Sans', system-ui, sans-serif;
  --color-bg:           #F2F2F2;
  --color-text:         #1a1a1a;
  --color-muted:        #555555;
  --color-border:       #E0E0E0;
  --color-accent:       #c8952b;
  --color-social-bg:    #1a1a1a;
  --color-social-hover: #c8952b;
  --color-popup-bg:     #ffffff;
  --shadow-popup:       0 8px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ════════════════════════════════════════════════
   SHELL
════════════════════════════════════════════════ */
.footer {
  background: var(--color-bg);
  padding: 40px 0 0;
  font-family: var(--font);
  position: relative;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 48px;
}

/* ════════════════════════════════════════════════
   TOP ROW
════════════════════════════════════════════════ */
.footer-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px;
  padding-bottom: 32px;
}

.footer-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.footer-logo {
  width: 140px;
  object-fit: contain;
}

.footer-addresses {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-address {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--color-muted);
  line-height: 1.6;
}

.addr-flag {
  font-size: 1.4rem;
  flex-shrink: 0;
  margin-top: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.footer-contact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
}

.contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.83rem;
  color: var(--color-muted);
  text-decoration: none;
  transition: color 0.2s;
}

.contact-item:hover { color: var(--color-text); }
.contact-item i { font-size: 0.8rem; opacity: 0.7; }

.contact-sep {
  display: inline-block;
  width: 1px;
  height: 14px;
  background: var(--color-border);
}

.footer-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
  flex-shrink: 0;
}

/* ════════════════════════════════════════════════
   BOUTON EXPERT + POPUP WRAP
════════════════════════════════════════════════ */
.expert-wrap {
  position: relative;
}

/* Bouton */
.btn-expert {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border: none;
  border-radius: 100px;
  background: #F3C17D;
  color: #2F2113;
  font-family: var(--font);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(243, 193, 125, 0.3);
}

.btn-expert i {
  color: #2F2113;
  font-size: 0.85rem;
  transition: color 0.2s;
}

.btn-expert:hover,
.btn-expert.is-active {
  background: #E8B56F;
  color: #2F2113;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(243, 193, 125, 0.4);
}

.btn-expert:hover i,
.btn-expert.is-active i {
  color: #2F2113;
}

/* ════════════════════════════════════════════════
   POPUP FLOTTANT
════════════════════════════════════════════════ */
.expert-popup {
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  z-index: 9999;
  background: var(--color-popup-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-popup);
  padding: 8px 0;
  min-width: 340px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Ligne du popup ── */
.popup-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}

.popup-row:hover {
  background: #fafaf8;
}

.popup-row--last {
  border-bottom: none;
}

/* Icône ronde */
.popup-row__icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.popup-row__icon--whatsapp {
  background: #e8f8f0;
  color: #25d366;
}

.popup-row__icon--phone {
  background: #f0f0f0;
  color: #444;
}

.popup-row__icon--email {
  background: #f0f0f0;
  color: #444;
}

/* Texte info */
.popup-row__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.popup-row__title {
  font-family: var(--font);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.popup-row__sub {
  font-family: var(--font);
  font-size: 0.75rem;
  color: var(--color-muted);
}

/* Bouton d'action */
.popup-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1.5px solid var(--color-border);
  border-radius: 8px;
  background: #fff;
  color: var(--color-text);
  font-family: var(--font);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  flex-shrink: 0;
}

.popup-action-btn:hover {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.popup-action-btn.is-copied {
  background: #e8f5e9;
  border-color: #2e7d32;
  color: #2e7d32;
}

.popup-action-btn i {
  font-size: 0.72rem;
}

/* ── Transition drop ── */
.popup-drop-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.popup-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.popup-drop-enter-from,
.popup-drop-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

/* ════════════════════════════════════════════════
   APP STORE BUTTONS
════════════════════════════════════════════════ */
.app-buttons {
  display: flex;
  gap: 12px;
}

.app-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: #fff;
  border: 1.5px solid var(--color-border);
  border-radius: 12px;
  text-decoration: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.app-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.10);
}

.app-button i {
  font-size: 26px;
  color: var(--color-text);
}

.btn-text {
  display: flex;
  flex-direction: column;
}

.btn-text__small {
  font-size: 9px;
  color: var(--color-muted);
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
}

.btn-text__large {
  font-size: 15px;
  color: var(--color-text);
  font-weight: 700;
  line-height: 1.3;
}

/* ════════════════════════════════════════════════
   SOCIAL ICONS
════════════════════════════════════════════════ */
.social-icons {
  display: flex;
  gap: 10px;
}

.social-icon {
  width: 38px;
  height: 38px;
  background: var(--color-social-bg);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-decoration: none;
  font-size: 15px;
  transition: background 0.2s ease, transform 0.2s ease;
}

.social-icon:hover {
  background: var(--color-social-hover);
  transform: translateY(-3px);
}

/* ════════════════════════════════════════════════
   BOTTOM
════════════════════════════════════════════════ */
.footer-bottom {
  border-top: 1px solid var(--color-border);
  padding: 18px 0;
  text-align: center;
}

.footer-copyright {
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-muted);
}

/* ════════════════════════════════════════════════
   RESPONSIVE — Tablet (≤ 900px)
════════════════════════════════════════════════ */
@media (max-width: 900px) {
  .footer-container { padding: 0 24px; }

  .footer-top {
    flex-direction: column;
    gap: 32px;
  }

  .footer-right {
    align-items: flex-start;
    width: 100%;
  }

  /* Popup s'ouvre vers le haut-gauche sur tablet */
  .expert-popup {
    bottom: calc(100% + 12px);
    right: auto;
    left: 0;
    min-width: 300px;
  }

  .app-buttons { width: 100%; }
  .app-button  { flex: 1; }
  .social-icons { width: 100%; }
}

/* ════════════════════════════════════════════════
   RESPONSIVE — Mobile (≤ 600px)
════════════════════════════════════════════════ */
@media (max-width: 600px) {
  .footer-container { padding: 0 16px; }
  .footer-logo      { width: 110px; }

  .app-buttons      { flex-direction: column; }

  .contact-sep { display: none; }

  .footer-contact {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .btn-expert {
    width: 100%;
    justify-content: center;
  }

  /* Popup pleine largeur sur mobile */
  .expert-popup {
    left: 0;
    right: 0;
    min-width: unset;
    width: 100%;
    border-radius: 12px;
  }

  .social-icons { justify-content: flex-start; }
}
</style>